import { useState, useEffect, useRef } from "react";
import { Star, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import GoogleRatingBadge from "@/components/GoogleRatingBadge";
import { googleReviews } from "@/data/googleReviews";
import Autoplay from "embla-carousel-autoplay";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";

export default function TestimonialsSection() {
  const { t } = useLanguage();
  const { localePath } = useLocalizedPath();
  const autoplayPlugin = useRef(
    Autoplay({ delay: 5000, stopOnInteraction: true, stopOnMouseEnter: true })
  );
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!carouselApi) return;
    setCurrent(carouselApi.selectedScrollSnap());
    carouselApi.on("select", () => {
      setCurrent(carouselApi.selectedScrollSnap());
    });
  }, [carouselApi]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") carouselApi?.scrollPrev();
      else if (e.key === "ArrowRight") carouselApi?.scrollNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [carouselApi]);

  return (
    <section className="section bg-muted/50 py-16 overflow-hidden">
      <div className="container">
        <div className="text-center max-w-4xl mx-auto mb-12">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="flex flex-col items-end gap-[3px]" aria-hidden="true">
              <span className="w-12 h-px bg-gold/60" />
              <span className="w-8 h-px bg-gold/60" />
              <span className="w-5 h-px bg-gold/60" />
            </div>
            <p className="text-gold text-sm tracking-[0.3em] uppercase">
              {t.testimonials.subtitle}
            </p>
            <div className="flex flex-col items-start gap-[3px]" aria-hidden="true">
              <span className="w-12 h-px bg-gold/60" />
              <span className="w-8 h-px bg-gold/60" />
              <span className="w-5 h-px bg-gold/60" />
            </div>
          </div>
          <h2 className="text-display text-3xl md:text-4xl lg:text-5xl text-foreground mb-4">
            {t.testimonials.title}
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl leading-relaxed">
            {t.testimonials.description}
          </p>
          <div className="flex justify-center mt-4">
            <GoogleRatingBadge />
          </div>
        </div>

        <div className="space-y-6">
          <Carousel
            opts={{ align: "center", loop: true, containScroll: false }}
            plugins={[autoplayPlugin.current]}
            setApi={setCarouselApi}
            className="w-full max-w-6xl mx-auto px-12"
          >
            <CarouselContent className="-ml-2 md:-ml-4">
              {googleReviews.map((review, index) => {
                const isCenter = index === current;
                return (
                  <CarouselItem
                    key={review.id}
                    className={`pl-2 md:pl-4 basis-full md:basis-1/3 lg:basis-1/5 transition-all duration-700 ease-out ${
                      isCenter
                        ? "opacity-100 scale-100 z-10 blur-0"
                        : "opacity-25 scale-90 blur-[2px]"
                    }`}
                  >
                    <div className="glass-card p-6 h-full flex flex-col">
                      <div className="flex mb-3">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-4 w-4 ${i < review.rating ? "fill-gold text-gold" : "text-muted-foreground"}`}
                          />
                        ))}
                      </div>
                      <blockquote className="flex-1 text-sm text-muted-foreground italic mb-4 line-clamp-5">
                        "{review.content}"
                      </blockquote>
                      <div className="mt-auto flex items-center gap-3">
                        {review.avatar ? (
                          <img src={review.avatar} alt={review.name} className="w-8 h-8 rounded-full object-cover shrink-0" loading="lazy" />
                        ) : (
                          <span className={`${review.initialColor} text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold shrink-0`}>
                            {review.initial}
                          </span>
                        )}
                        <div>
                          <h4 className="font-semibold text-sm">{review.name}</h4>
                          <p className="text-xs text-muted-foreground">Google</p>
                        </div>
                      </div>
                    </div>
                  </CarouselItem>
                );
              })}
            </CarouselContent>
            <CarouselPrevious className="left-0 hover:scale-110 transition-transform z-20" />
            <CarouselNext className="right-0 hover:scale-110 transition-transform z-20" />
          </Carousel>

          {/* Dots */}
          <div className="flex justify-center gap-2">
            {googleReviews.map((_, index) => (
              <button
                key={index}
                onClick={() => carouselApi?.scrollTo(index)}
                className={`transition-all duration-300 rounded-full ${
                  index === current
                    ? "w-8 h-2 bg-gold"
                    : "w-2 h-2 bg-muted-foreground/30 hover:bg-muted-foreground/50"
                }`}
                aria-label={`Aller au témoignage ${index + 1}`}
              />
            ))}
          </div>

          {/* CTA to dedicated page */}
          <div className="flex justify-center pt-2">
            <Link
              to={localePath("/avis")}
              className="inline-flex items-center gap-2 text-sm text-gold hover:text-gold/80 transition-colors font-medium uppercase tracking-wider"
            >
              {(t as any).testimonials?.viewAll || "Voir tous les avis"}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
